import React, { useEffect, useState } from "react";
import styled from "styled-components";
import EditableInput from "@uiw/react-color-editable-input";
import Hue from "@uiw/react-color-hue";
import Saturation from "@uiw/react-color-saturation";
import { hsvaToHex, hexToHsva, hsvaToRgba } from "@uiw/color-convert";
import Checkbox from "../Checkbox";
import Label from "../Label";
import { colorFunctionalBlack, colorFunctionalWhite, colorMiscGray80, colorMiscGray90, } from "@rhumbix/rmbx_design_system_design_tokens";
const marginBottom = 25;
/**
 * A color picker widget similar to the ChromePicker component from react-color:
 * http://casesandberg.github.io/react-color/#examples
 * @constructor
 */
const ColorPicker = ({ hueStyle, initialColor, initialHue, initialOutline, onColorChange, saturationStyle, showOutlineCheckbox, showPreview, statusName, }) => {
    const [color, setColor] = useState({ h: 0, s: 0, v: 0, a: 1 });
    const [hue, setHue] = useState({ h: 0 });
    const [isOutline, setIsOutline] = useState(initialOutline || false);
    useEffect(() => {
        if (initialHue)
            setHue({ h: initialHue });
        if (initialColor) {
            const hsvaInitColor = hexToHsva(initialColor);
            setColor(hsvaInitColor);
            setHue({ h: hsvaInitColor.h });
        }
    }, []);
    useEffect(() => {
        if (color) {
            onColorChange(hsvaToHex(color), isOutline);
        }
    }, [color]);
    // Get a font color based on the background. Follows W3C compliance:
    // http://tinyurl.com/yn6w5hcj
    const getFontColor = () => {
        if (!color)
            return "#fff";
        const rgbColor = hsvaToRgba(color);
        const uicolors = [rgbColor.r / 255, rgbColor.g / 255, rgbColor.b / 255];
        const c = uicolors.map(col => {
            if (col <= 0.03928) {
                return col / 12.92;
            }
            return Math.pow((col + 0.055) / 1.055, 2.4);
        });
        const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
        return L > 0.179 ? colorFunctionalBlack : colorFunctionalWhite;
    };
    return (React.createElement(OuterContainer, null,
        React.createElement(PickerHeader, null, "Set Color:"),
        React.createElement(Saturation, { onChange: setColor, hsva: color, style: {
                ...{ width: 450, height: 260, marginBottom, borderStyle: "solid", borderWidth: "1px" },
                ...(saturationStyle || {}),
            } }),
        React.createElement(Hue, { style: { ...{ width: 450, marginBottom }, ...(hueStyle || {}) }, hue: hue.h, onChange: value => {
                setHue(value);
                setColor({ ...color, ...value });
            } }),
        showOutlineCheckbox && (React.createElement(CheckboxContainer, null,
            React.createElement(Checkbox, { id: "is-outline-checkbox", checked: isOutline, onChange: () => {
                    setIsOutline(!isOutline);
                    onColorChange(hsvaToHex(color), !isOutline);
                } }),
            React.createElement(Label, { htmlFor: "is-outline-checkbox", style: { fontWeight: 400, fontSize: "14px", marginLeft: "8px" } }, "Outline Only"))),
        React.createElement(InputAndPreviewContainer, null,
            React.createElement(EditableInput, { placement: "left", inputStyle: {
                    width: 152,
                    height: 21,
                    fontSize: 14,
                    color: colorFunctionalBlack,
                    boxShadow: `${colorFunctionalBlack} 0px 0px 0px 1px inset`,
                }, style: { alignSelf: "flex-start", marginBottom, color: colorFunctionalBlack }, label: "Selection", labelStyle: {
                    marginRight: "8px",
                    fontWeight: 400,
                    fontSize: 14,
                    color: colorFunctionalBlack,
                }, value: hsvaToHex(color).toUpperCase(), onChange: (_, newColor) => {
                    const newColorString = typeof newColor === "number" ? newColor.toString(16).padStart(6, "0") : newColor;
                    const a = parseInt(newColorString, 16);
                    // Test for a valid hex value
                    if (a.toString(16).padStart(6, "0") === newColorString) {
                        const hsva = hexToHsva(newColorString);
                        setColor(hsva);
                        setHue({ h: hsva.h });
                    }
                } }),
            showPreview && (React.createElement(PreviewContainer, null,
                React.createElement(StyledLabel, null, "Preview"),
                React.createElement(ColorPreview, { style: {
                        backgroundColor: isOutline ? colorFunctionalWhite : hsvaToHex(color),
                        borderWidth: isOutline ? 2 : 0,
                        borderStyle: isOutline ? "solid" : "none",
                        borderColor: isOutline ? hsvaToHex(color) : colorFunctionalWhite,
                        borderRadius: isOutline ? 2 : 0,
                        color: isOutline ? hsvaToHex(color) : getFontColor(),
                    } }, statusName || "My Awesome Status"))))));
};
export default ColorPicker;
const OuterContainer = styled.div `
    margin-left: 8px;
`;
const PickerHeader = styled.div `
    font-weight: 500;
    font-size: 20px;
    font-color: ${colorMiscGray80};
    margin-bottom: 20px;
`;
const CheckboxContainer = styled.div `
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
`;
const InputAndPreviewContainer = styled.div `
    display: flex;
    flex-direction: column;
`;
const PreviewContainer = styled.div `
    display: flex;
    flex-direction: row;
    margin-bottom: ${marginBottom}px;
`;
const StyledLabel = styled.div `
    align-items: center;
    color: ${colorMiscGray90};
    display: flex;
    flex-direction: row;
    font-family: Roboto;
    font-size: 16px;
    font-weight: 500;
    margin-right: 8px;
`;
const ColorPreview = styled.div `
    align-items: center;
    display: flex;
    width: 180px;
    height: 27px;
    font-weight: 500;
    font-size: 14px;
    justify-content: center;
`;
